import { apiRequest, getStoredToken } from './request';

export { AppError, getStoredToken, setStoredToken } from './request';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8080';

// --- Auth ---
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  planId: string;
  subStatus: 'active' | 'expired' | 'cancelled' | '';
  subRenewsAt: string;
  hasActiveAccess: boolean;
  /** True when a LemonSqueezy subscription exists (cancel/resume is possible). */
  hasSubscription: boolean;
  /** False until first-run onboarding finishes (or legacy plan/sub exists). */
  onboardingCompleted: boolean;
  aiCredits: { balance: number; grantedMonth: string };
  createdAt: string;
}

export interface AuthResponse {
  token: string;
  expiresAt: string;
  user: AuthUser;
}

export async function register(email: string, password: string, name: string): Promise<AuthResponse> {
  return apiRequest<AuthResponse>(`${API_BASE}/api/v1/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, name }),
  });
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  return apiRequest<AuthResponse>(`${API_BASE}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
}

let meInflight: Promise<AuthUser> | null = null;

export function invalidateMe(): void {
  meInflight = null;
}

export async function getMe(): Promise<AuthUser> {
  if (!meInflight) {
    meInflight = apiRequest<AuthUser>(`${API_BASE}/api/v1/auth/me`).finally(() => {
      meInflight = null;
    });
  }
  return meInflight;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/auth/me/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

/** Marks first-run onboarding complete on the user (no preference payload). */
export async function completeOnboarding(): Promise<AuthUser> {
  invalidateMe();
  return apiRequest<AuthUser>(`${API_BASE}/api/v1/auth/me/onboarding`, {
    method: 'PATCH',
  });
}

export function isLoggedIn(): boolean {
  return !!getStoredToken();
}

// --- Plans & billing ---
export interface Plan {
  id: string;
  name: string;
  monthlyPriceUsd: number;
  maxInstagramAccounts: number;
  monthlyAiCredits: number;
}

export interface CreditPack {
  credits: number;
  priceUsd: number;
}

export interface PlansCatalog {
  plans: Plan[];
  creditPack: CreditPack;
  catalogVersion: number;
}

let plansCatalogInflight: Promise<PlansCatalog> | null = null;

export async function listPlansCatalog(): Promise<PlansCatalog> {
  if (!plansCatalogInflight) {
    plansCatalogInflight = apiRequest<PlansCatalog>(`${API_BASE}/api/v1/plans`)
      .then((res) => ({
        plans: res?.plans ?? [],
        creditPack: res?.creditPack ?? { credits: 100, priceUsd: 9 },
        catalogVersion: res?.catalogVersion ?? 0,
      }))
      .finally(() => {
        plansCatalogInflight = null;
      });
  }
  return plansCatalogInflight;
}

export async function listPlans(): Promise<Plan[]> {
  const catalog = await listPlansCatalog();
  return catalog.plans;
}

export async function createCheckout(planId: string): Promise<string> {
  const res = await apiRequest<{ url: string }>(`${API_BASE}/api/v1/billing/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ planId }),
  });
  return res.url;
}

export async function cancelSubscription(): Promise<AuthUser> {
  invalidateMe();
  return apiRequest<AuthUser>(`${API_BASE}/api/v1/billing/subscription/cancel`, { method: 'POST' });
}

export async function resumeSubscription(): Promise<AuthUser> {
  invalidateMe();
  return apiRequest<AuthUser>(`${API_BASE}/api/v1/billing/subscription/resume`, { method: 'POST' });
}

export async function getCreditsBalance(): Promise<{ balance: number }> {
  return apiRequest<{ balance: number }>(`${API_BASE}/api/v1/credits`);
}

export interface CreditTransaction {
  id: string;
  userId: string;
  delta: number;
  reason: string;
  balanceAfter: number;
  createdAt: string;
}

export async function getCreditsHistory(): Promise<CreditTransaction[]> {
  const res = await apiRequest<{ items: CreditTransaction[] }>(`${API_BASE}/api/v1/credits/history`);
  return res?.items ?? [];
}

export async function createCreditsCheckout(): Promise<string> {
  const res = await apiRequest<{ url: string }>(`${API_BASE}/api/v1/billing/credits/checkout`, { method: 'POST' });
  return res.url;
}

// --- Instagram accounts ---
export interface InstagramAccount {
  id: string;
  userId: string;
  igUserId: string;
  igUsername: string;
  pageId: string;
  pageName: string;
  profilePictureUrl: string;
  tokenStatus: 'valid' | 'expired';
  connectedAt: string;
  updatedAt: string;
}

let accountsInflight: Promise<InstagramAccount[]> | null = null;

export async function listInstagramAccounts(): Promise<InstagramAccount[]> {
  if (!accountsInflight) {
    accountsInflight = apiRequest<{ accounts: InstagramAccount[] }>(`${API_BASE}/api/v1/instagram-accounts`)
      .then((res) => res?.accounts ?? [])
      .finally(() => {
        accountsInflight = null;
      });
  }
  return accountsInflight;
}

export async function getInstagramOAuthURL(): Promise<string> {
  const res = await apiRequest<{ url: string }>(`${API_BASE}/api/v1/instagram-accounts/oauth-url`);
  return res.url;
}

export async function disconnectInstagramAccount(accountId: string): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}`, { method: 'DELETE' });
}

// --- Reference sources ---
export interface ReferenceSource {
  id: string;
  instagramAccountId: string;
  username: string;
  profilePictureUrl?: string;
  status: 'pending' | 'analyzed' | 'failed';
  analysisSummary: string;
  lastAnalyzedAt: string;
  createdAt: string;
}

export async function listReferenceSources(accountId: string): Promise<ReferenceSource[]> {
  const res = await apiRequest<{ referenceSources: ReferenceSource[] }>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/reference-sources`
  );
  return res?.referenceSources ?? [];
}

export async function createReferenceSource(accountId: string, username: string): Promise<ReferenceSource> {
  return apiRequest<ReferenceSource>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/reference-sources`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username }) }
  );
}

export async function analyzeReferenceSource(accountId: string, refId: string): Promise<ReferenceSource> {
  return apiRequest<ReferenceSource>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/reference-sources/${encodeURIComponent(refId)}/analyze`,
    { method: 'POST' }
  );
}

export async function deleteReferenceSource(accountId: string, refId: string): Promise<void> {
  await apiRequest<void>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/reference-sources/${encodeURIComponent(refId)}`,
    { method: 'DELETE' }
  );
}

// --- Content drafts ---
export interface ContentDraft {
  id: string;
  instagramAccountId: string;
  referenceSourceId?: string;
  source?: 'ai' | 'manual';
  status: 'pending_review' | 'approved' | 'rejected' | 'scheduled' | 'published';
  caption: string;
  hashtags: string[];
  imagePrompt: string;
  imageUrl: string;
  imageUrls?: string[];
  creditsCost: number;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

export async function listContentDrafts(accountId: string, status?: string): Promise<ContentDraft[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const res = await apiRequest<{ contentDrafts: ContentDraft[] }>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/content-drafts${qs}`
  );
  return res?.contentDrafts ?? [];
}

export async function generateContentDraft(accountId: string, referenceSourceId?: string): Promise<ContentDraft> {
  return apiRequest<ContentDraft>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/content-drafts/generate`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ referenceSourceId: referenceSourceId ?? '' }) }
  );
}

export async function regenerateContentDraft(accountId: string, draftId: string): Promise<ContentDraft> {
  return apiRequest<ContentDraft>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/content-drafts/${encodeURIComponent(draftId)}/regenerate`,
    { method: 'POST' }
  );
}

export async function approveContentDraft(accountId: string, draftId: string): Promise<ContentDraft> {
  return apiRequest<ContentDraft>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/content-drafts/${encodeURIComponent(draftId)}/approve`,
    { method: 'POST' }
  );
}

export async function rejectContentDraft(accountId: string, draftId: string, reason?: string): Promise<ContentDraft> {
  return apiRequest<ContentDraft>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/content-drafts/${encodeURIComponent(draftId)}/reject`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: reason ?? '' }) }
  );
}

export async function updateContentDraft(accountId: string, draftId: string, caption: string, hashtags: string[]): Promise<ContentDraft> {
  return apiRequest<ContentDraft>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/content-drafts/${encodeURIComponent(draftId)}`,
    { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ caption, hashtags }) }
  );
}

// --- Scheduled posts ---
export interface ScheduledPost {
  id: string;
  contentDraftId: string;
  instagramAccountId: string;
  scheduledAt: string;
  status: 'scheduled' | 'publishing' | 'published' | 'failed';
  igMediaId?: string;
  publishedAt?: string;
  lastError?: string;
  createdAt: string;
  caption?: string;
  hashtags?: string[];
  imageUrl?: string;
  imageUrls?: string[];
  source?: 'ai' | 'manual';
}

export async function listScheduledPosts(accountId: string): Promise<ScheduledPost[]> {
  const res = await apiRequest<{ scheduledPosts: ScheduledPost[] }>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/scheduled-posts`
  );
  return res?.scheduledPosts ?? [];
}

export async function createManualScheduledPost(
  accountId: string,
  input: { caption: string; hashtags: string; scheduledAt: string; images: File[] }
): Promise<{ scheduledPost: ScheduledPost; contentDraft: ContentDraft }> {
  const form = new FormData();
  form.append('caption', input.caption);
  form.append('hashtags', input.hashtags);
  form.append('scheduledAt', input.scheduledAt);
  for (const file of input.images) {
    form.append('images', file);
  }
  return apiRequest(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/scheduled-posts/manual`,
    { method: 'POST', body: form }
  );
}

export async function updateScheduledPost(
  accountId: string,
  postId: string,
  input: { caption: string; hashtags: string; scheduledAt: string; images?: File[] }
): Promise<ScheduledPost> {
  const form = new FormData();
  form.append('caption', input.caption);
  form.append('hashtags', input.hashtags);
  form.append('scheduledAt', input.scheduledAt);
  if (input.images?.length) {
    for (const file of input.images) {
      form.append('images', file);
    }
  }
  return apiRequest(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/scheduled-posts/${encodeURIComponent(postId)}`,
    { method: 'PUT', body: form }
  );
}

export async function createScheduledPost(accountId: string, draftId: string, scheduledAt?: string): Promise<ScheduledPost> {
  return apiRequest<ScheduledPost>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/scheduled-posts`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ draftId, scheduledAt: scheduledAt ?? '' }) }
  );
}

export async function suggestScheduleTime(accountId: string): Promise<string> {
  const res = await apiRequest<{ suggestedAt: string }>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/scheduled-posts/suggest-time`,
    { method: 'POST' }
  );
  return res.suggestedAt;
}

export async function cancelScheduledPost(accountId: string, postId: string): Promise<void> {
  await apiRequest<void>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/scheduled-posts/${encodeURIComponent(postId)}`,
    { method: 'DELETE' }
  );
}

export async function publishScheduledPostNow(accountId: string, postId: string): Promise<ScheduledPost> {
  return apiRequest<ScheduledPost>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/scheduled-posts/${encodeURIComponent(postId)}/publish`,
    { method: 'POST' }
  );
}

// --- Metrics ---
export interface TopPostSummary {
  scheduledPostId: string;
  engagementScore: number;
  likes: number;
  comments: number;
}

export interface AccountMetricsSummary {
  bestHourUtc: number;
  bestHourEndUtc: number;
  isDefaultBestHour: boolean;
  topPosts: TopPostSummary[];
  totalLikes: number;
  totalComments: number;
  totalReach: number;
  totalImpressions: number;
}

export function formatBestHour(summary: Pick<AccountMetricsSummary, 'bestHourUtc' | 'bestHourEndUtc' | 'isDefaultBestHour'>): string {
  const start = `${String(summary.bestHourUtc).padStart(2, '0')}:00`;
  if (!summary.isDefaultBestHour || summary.bestHourEndUtc === summary.bestHourUtc) {
    return start;
  }
  const end = `${String(summary.bestHourEndUtc).padStart(2, '0')}:00`;
  return `${start}–${end}`;
}

export async function getAccountMetricsSummary(accountId: string): Promise<AccountMetricsSummary> {
  return apiRequest<AccountMetricsSummary>(
    `${API_BASE}/api/v1/instagram-accounts/${encodeURIComponent(accountId)}/metrics/summary`
  );
}

// --- Notifications ---

export interface Notification {
  id: string;
  type: 'post_published' | 'post_failed';
  title: string;
  message: string;
  relatedScheduledPostId?: string;
  read: boolean;
  createdAt: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
}

export async function listNotifications(): Promise<NotificationsResponse> {
  const res = await apiRequest<NotificationsResponse>(`${API_BASE}/api/v1/notifications`);
  return { notifications: res?.notifications ?? [], unreadCount: res?.unreadCount ?? 0 };
}

export async function markNotificationRead(id: string): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' });
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/notifications/read-all`, { method: 'POST' });
}
